import React, { useMemo } from 'react';
import {
  formatKpiPercent,
  parseKpiPercent,
  useKpiCountUp,
} from '../utils/useKpiCountUp';
import MetricInfoIcon from './MetricInfoIcon';

const SPARKLINE_WIDTH = 120;
const SPARKLINE_HEIGHT = 32;
const SPARKLINE_PAD = 2;

function buildSparklinePath(values) {
  if (!values?.length) return '';
  const max = Math.max(1, ...values);
  const step = values.length > 1
    ? (SPARKLINE_WIDTH - SPARKLINE_PAD * 2) / (values.length - 1)
    : 0;
  const points = values.map((value, index) => {
    const x = SPARKLINE_PAD + index * step;
    const y = SPARKLINE_HEIGHT - SPARKLINE_PAD
      - ((value / max) * (SPARKLINE_HEIGHT - SPARKLINE_PAD * 2));
    return [x, y];
  });
  return points.map(([x, y], index) => (
    `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`
  )).join(' ');
}

function KpiSparkline({ values = [], trendDirection = 'flat' }) {
  const pathD = useMemo(() => buildSparklinePath(values), [values]);
  if (!pathD) return null;

  const trendClass = trendDirection === 'up'
    ? 'kpi-sparkline--up'
    : trendDirection === 'down'
      ? 'kpi-sparkline--down'
      : 'kpi-sparkline--flat';

  return (
    <svg
      className={`kpi-sparkline ${trendClass}`}
      viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={pathD} className="kpi-sparkline__line" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

const KPI_VALUE_DELAY_BASE_MS = 220;
const KPI_STAGGER_MS = 65;

const shortCompanyLabel = (name, maxWords = 2) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'Competitor';
  return parts.slice(0, maxWords).join(' ');
};

const staggerDelay = (staggerIndex, extra = 0) => (
  `${KPI_VALUE_DELAY_BASE_MS + extra + staggerIndex * KPI_STAGGER_MS}ms`
);

function AnimatedKpiValue({ value, staggerIndex = 0, animate = true }) {
  const delay = KPI_VALUE_DELAY_BASE_MS + staggerIndex * KPI_STAGGER_MS;
  const { display, isNumeric } = useKpiCountUp(value, { delay, animate });

  return (
    <span
      className={`kpi-banner-value${animate && isNumeric ? ' kpi-banner-value--enter' : ''}`}
      style={animate && isNumeric ? { animationDelay: staggerDelay(staggerIndex) } : undefined}
    >
      {display}
    </span>
  );
}

const KPI_DELTA_DELAY_OFFSET_MS = 80;

function KpiPercentDelta({ compareChip, staggerIndex = 0, animate = true }) {
  const { status, percentLabel } = compareChip;
  const modifier = status === 'better' ? 'kpi-delta--better' : 'kpi-delta--worse';
  const parsed = parseKpiPercent(percentLabel);
  const delay = KPI_VALUE_DELAY_BASE_MS + KPI_DELTA_DELAY_OFFSET_MS + staggerIndex * KPI_STAGGER_MS;
  const { display } = useKpiCountUp(
    parsed?.magnitude ?? percentLabel,
    { delay, animate: animate && Boolean(parsed) },
  );
  const displayLabel = parsed
    ? formatKpiPercent(parsed.sign, Number(display) || 0, parsed.decimals)
    : percentLabel;

  return (
    <span
      className={`kpi-delta ${modifier}${animate && parsed ? ' kpi-delta--enter' : ''}`}
      style={animate && parsed ? { animationDelay: staggerDelay(staggerIndex, KPI_DELTA_DELAY_OFFSET_MS) } : undefined}
      aria-label={`${percentLabel} vs. competitors`}
    >
      <span className="kpi-delta__pct">{displayLabel}</span>
    </span>
  );
}

function KpiSubtitle({ subtitle, compareNote, subtitleSuffix, subtitleSuffixClassName = 'kpi-subtitle-detail' }) {
  if (compareNote) {
    return (
      <span className={`kpi-banner-subtitle${subtitleSuffix ? ' kpi-banner-subtitle--stacked' : ''}`}>
        <span className="kpi-compare-annotation">
          {compareNote.label}
          {' '}
          <span className="kpi-compare-annotation__value">{compareNote.benchmark}</span>
        </span>
        {subtitleSuffix ? (
          <span className={subtitleSuffixClassName}>{subtitleSuffix}</span>
        ) : null}
      </span>
    );
  }

  return (
    <span className="kpi-banner-subtitle" style={{ color: 'var(--text2)' }}>
      {subtitle}
    </span>
  );
}

export default function KPICard({
  title,
  titleTooltip,
  value,
  subtitle,
  style,
  cardClassName = '',
  productFocus,
  compareChip,
  compareNote,
  subtitleSuffix,
  subtitleSuffixClassName,
  staggerIndex = 0,
  animate = true,
  sparkline,
  trendDirection = 'flat',
  trendIcon: TrendIcon,
}) {
  const bannerCardClass = [
    'kpi-banner-card',
    sparkline?.length ? 'kpi-banner-card--with-sparkline' : '',
    cardClassName,
  ].filter(Boolean).join(' ');
  if (productFocus?.productLabel) {
    const {
      productLabel,
      subjectCount,
      competitorName,
      competitorCount,
    } = productFocus;
    const showCompare = subjectCount !== null && subjectCount !== undefined
      && competitorCount !== null && competitorCount !== undefined;

    return (
      <div className="kpi-banner-card kpi-banner-card--product-focus" style={style}>
        <span className="kpi-banner-title">{title}</span>
        <p className="kpi-product-focus__label" title={productLabel}>
          {productLabel}
        </p>
        {showCompare ? (
          <div className="kpi-product-focus__compare">
            <div className="kpi-product-focus__side">
              <span className="kpi-product-focus__who">You</span>
              <span className="kpi-product-focus__count">{subjectCount}</span>
            </div>
            <span className="kpi-product-focus__divider" aria-hidden="true">·</span>
            <div className="kpi-product-focus__side">
              <span
                className="kpi-product-focus__who"
                title={competitorName || 'Competitor'}
              >
                {shortCompanyLabel(competitorName)}
              </span>
              <span className="kpi-product-focus__count">{competitorCount}</span>
            </div>
          </div>
        ) : (
          <div className="kpi-product-focus__compare kpi-product-focus__compare--single">
            {subjectCount !== null && subjectCount !== undefined ? (
              <div className="kpi-product-focus__side">
                <span className="kpi-product-focus__who">You</span>
                <span className="kpi-product-focus__count">{subjectCount}</span>
              </div>
            ) : (
              <div className="kpi-product-focus__side">
                <span
                  className="kpi-product-focus__who"
                  title={competitorName || 'Competitor'}
                >
                  {shortCompanyLabel(competitorName)}
                </span>
                <span className="kpi-product-focus__count">{competitorCount}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={bannerCardClass} style={style}>
      <span className={`kpi-banner-title${titleTooltip ? ' kpi-banner-title--with-info' : ''}`}>
        <span className="kpi-banner-title__label">{title}</span>
        {titleTooltip ? (
          <MetricInfoIcon
            title={title}
            text={titleTooltip}
            className="geo-metric-info geo-metric-info--kpi"
            iconSize={12}
          />
        ) : null}
      </span>
      <div className="kpi-banner-value-row">
        <AnimatedKpiValue value={value} staggerIndex={staggerIndex} animate={animate} />
        {compareChip?.status && compareChip?.percentLabel ? (
          <span className="kpi-delta-slot">
            <KpiPercentDelta
              compareChip={compareChip}
              staggerIndex={staggerIndex}
              animate={animate}
            />
          </span>
        ) : null}
      </div>
      <KpiSubtitle
        subtitle={subtitle}
        compareNote={compareNote}
        subtitleSuffix={subtitleSuffix}
        subtitleSuffixClassName={subtitleSuffixClassName}
      />
      {sparkline?.length ? (
        <div className="kpi-banner-sparkline-row">
          <KpiSparkline values={sparkline} trendDirection={trendDirection} />
          {TrendIcon ? (
            <span
              className={`kpi-trend-indicator kpi-trend-indicator--${trendDirection}`}
              aria-label={`Trend ${trendDirection}`}
            >
              <TrendIcon size={16} strokeWidth={2} aria-hidden="true" />
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
